import { useForm } from "react-hook-form"
import { toast } from "sonner"
import { useTranslation } from "react-i18next"
import { Link, useSearch } from "wouter"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useAdminAuth } from "@/hooks/useAdminAuth"
import { api, ApiRequestError } from "@/lib/api"
import { inputClass, submitBtnClass } from "@/lib/styleClasses"
import { AdminMarketingPanel } from "@/components/admin/AdminMarketingPanel"
import { LanguageSwitcher } from "@/components/LanguageSwitcher"

interface LoginForm {
    email: string
    password: string
}

export const AdminLoginPage = () => {
    const { t } = useTranslation()
    const { login, isLoggingIn } = useAdminAuth()
    const search = useSearch()
    const params = new URLSearchParams(search)
    const inviteToken = params.get("invite")
    const { register, handleSubmit, formState: { errors } } = useForm<LoginForm>()

    const onSubmit = async (data: LoginForm) => {
        try {
            await login(data)
            if (inviteToken) {
                try {
                    await api.post(`/invitations/${inviteToken}/accept`, {})
                } catch {
                    // Already a member or expired — proceed to admin anyway
                }
            }
        } catch (error) {
            const message = error instanceof ApiRequestError ? error.message : t("admin.login.toastLoginFailed")
            toast.error(message)
        }
    }

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
            <div className="w-full max-w-5xl flex justify-end mb-3">
                <LanguageSwitcher />
            </div>
            <div className="flex flex-col lg:flex-row gap-6 lg:items-start w-full max-w-5xl">
            <div className="bg-white rounded-[40px] border border-slate-100 shadow-xl p-10 w-full max-w-md">
                <div className="mb-8 text-center">
                    <h1 className="text-3xl font-black tracking-tight text-slate-900 mb-2">{t("admin.login.title")}</h1>
                    <p className="text-slate-500 font-bold">{t("admin.login.subtitle")}</p>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <div className="space-y-1">
                        <label className="text-xs font-black uppercase tracking-widest text-slate-600">
                            {t("admin.login.email")}
                        </label>
                        <Input
                            type="email"
                            placeholder="admin@example.com"
                            className={inputClass}
                            error={errors.email?.message}
                            {...register("email", {
                                required: t("admin.login.emailRequired"),
                                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: t("admin.login.emailInvalid") },
                            })}
                        />
                    </div>

                    <div className="space-y-1">
                        <label className="text-xs font-black uppercase tracking-widest text-slate-600">
                            {t("admin.login.password")}
                        </label>
                        <Input
                            type="password"
                            placeholder="••••••••"
                            className={inputClass}
                            error={errors.password?.message}
                            {...register("password", { required: t("admin.login.passwordRequired") })}
                        />
                    </div>

                    <Button
                        type="submit"
                        disabled={isLoggingIn}
                        className={submitBtnClass}
                    >
                        {isLoggingIn ? t("admin.login.signingIn") : t("admin.login.signIn")}
                    </Button>
                </form>

                <div className="mt-6 flex flex-col items-center gap-2 text-sm">
                    <Link to="/admin/forgot-password" className="font-bold text-slate-400 hover:text-slate-600 transition-colors">
                        {t("admin.login.forgotPassword")}
                    </Link>
                    <p className="text-slate-500">
                        {t("admin.login.noAccount")}{" "}
                        <Link to="/admin/register" className="font-black text-brand hover:text-brand-dark transition-colors">
                            {t("admin.login.register")}
                        </Link>
                    </p>
                </div>
            </div>
            <AdminMarketingPanel />
            </div>
        </div>
    )
}
